import { useRef, useState, type ComponentProps, type ReactNode, type RefObject } from 'react';
import { TravelerPickerField } from '../../shared/person/traveler_picker.js';
import {
  TravelerPickerFooter,
  TravelerPolicyRow,
} from '../../shared/person/traveler_picker_footer.js';
import { resolvePolicyTraveler } from '../../shared/person/traveler_policy.js';
import {
  HotelRoomsField,
  type HotelRoomsFieldProps,
  type HotelRoomsView,
} from './hotel_rooms_field.js';
import { toPerson } from './hotel_rooms_pickers.js';
import { findTraveler } from './hotel_rooms_travelers.js';

/** O pé do QUEM: quem rege a política (com 2+ hóspedes nomeados), o resumo
 *  anunciado e o «Pronto» — o pé do kit, o mesmo do aéreo e do ônibus. */
function RoomsFooter({
  field,
  summary,
  onChoosePolicy,
  onDone,
}: {
  readonly field: HotelRoomsFieldProps;
  readonly summary: string;
  readonly onChoosePolicy: () => void;
  readonly onDone: () => void;
}): ReactNode {
  const { copy, travelerIds, travelers, requesterId } = field;
  const policyId =
    travelerIds.length > 1
      ? resolvePolicyTraveler(
          travelerIds.map((id) => ({ id })),
          field.policyTravelerId,
          requesterId,
        )
      : undefined;
  const subject = policyId ? findTraveler(policyId, travelers) : undefined;
  const policy = policyId ? (
    <TravelerPolicyRow
      subject={subject ? toPerson(subject) : { id: policyId, name: policyId }}
      requesterId={requesterId}
      label={copy.policyTravelerLabel}
      youLabel={copy.policyRequesterLabel}
      disabled={field.disabled ?? false}
      onChoose={onChoosePolicy}
    />
  ) : undefined;
  return (
    <TravelerPickerFooter
      {...(policy ? { policy } : {})}
      summary={summary}
      doneLabel={copy.doneLabel}
      onDone={onDone}
    />
  );
}

/**
 * O campo QUEM do hotel inteiro, sobre o `TravelerPickerField` do kit — o
 * mesmo Popover do DS do aéreo e do `Onde`, e a mesma folha no palco estreito.
 * Dentro, a matriz de quartos (`HotelRoomsField`) com a tela controlada daqui:
 * o pé mostra a política e o «Pronto» só na visão geral, e a linha da
 * política leva à lista de quem a rege.
 *
 * Abrir e fechar são do host (a máquina de painéis da barra, a fileira da
 * Página do Hotel): o gatilho é dele, com o ref que ele também usa para
 * devolver o foco.
 */
export function HotelRoomsPickerField({
  open,
  onOpenChange,
  layout,
  title,
  backLabel,
  trigger,
  triggerRef,
  returnFocusTo,
  summary,
  onDone,
  field,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly layout: 'popover' | 'sheet';
  /** O título da folha e o nome acessível do popover. */
  readonly title: string;
  readonly backLabel: string;
  readonly trigger: (ref: RefObject<HTMLButtonElement | null>) => ReactNode;
  readonly triggerRef: RefObject<HTMLButtonElement | null>;
  readonly returnFocusTo?: ComponentProps<typeof TravelerPickerField>['returnFocusTo'];
  /** "5 hóspedes · 2 quartos" — o resumo do pé, o mesmo do gatilho. */
  readonly summary: string;
  /** O «Pronto». Ausente ⇒ só fecha. */
  readonly onDone?: () => void;
  /** O miolo: as props do `HotelRoomsField`, menos a tela e a largura. */
  readonly field: Omit<HotelRoomsFieldProps, 'view' | 'onViewChange' | 'fill'>;
}): ReactNode {
  const [view, setView] = useState<HotelRoomsView>({ kind: 'rooms' });
  // O foco ao abrir o popover pousa no painel, não no ⓘ do quarto (ver
  // `TravelerPickerField.initialFocus`); o Tab segue a ordem de sempre.
  const panelRef = useRef<HTMLDivElement>(null);
  const sheet = layout === 'sheet';
  const change = (next: boolean) => {
    // Fechar sempre volta à visão geral: reabrir não cai no meio de uma escolha.
    if (!next) setView({ kind: 'rooms' });
    onOpenChange(next);
  };
  return (
    <TravelerPickerField
      open={open}
      onOpenChange={change}
      title={title}
      backLabel={backLabel}
      layout={layout}
      trigger={trigger}
      triggerRef={triggerRef}
      {...(returnFocusTo ? { returnFocusTo } : {})}
      initialFocus={panelRef}
      {...(view.kind === 'rooms'
        ? {
            footer: (
              <RoomsFooter
                field={field}
                summary={summary}
                onChoosePolicy={() => {
                  setView({ kind: 'policy' });
                }}
                onDone={() => {
                  setView({ kind: 'rooms' });
                  if (onDone) onDone();
                  else onOpenChange(false);
                }}
              />
            ),
          }
        : {})}
    >
      {sheet ? (
        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-rail p-4 pr-[calc(1rem-var(--scrollbar-rail))]">
          <HotelRoomsField {...field} fill view={view} onViewChange={setView} />
        </div>
      ) : (
        <div ref={panelRef} tabIndex={-1} className="outline-none">
          <HotelRoomsField {...field} view={view} onViewChange={setView} />
        </div>
      )}
    </TravelerPickerField>
  );
}
